import { decryptJWT, encryptJWT } from "./jwt";
import type { OsNotificationPreference } from "./os-notification-policy";
import { buildUrl } from "./url";

export const OS_EMAIL_ACTION_PURPOSE = "os-email-action" as const;

/** Signed, no-login actions a broker can take from an OS email. */
export type OsEmailActionKind =
  | "snooze"
  | "mute_deal"
  | "not_interested"
  | "mute_kind"
  | "stop"
  | "manage";

export type OsEmailActionToken = {
  purpose: typeof OS_EMAIL_ACTION_PURPOSE;
  userId: string;
  action: OsEmailActionKind;
  dealId?: string;
  /** Preference flag to flip for `mute_kind`. */
  preference?: OsNotificationPreference;
};

const ACTION_PATHS: Record<OsEmailActionKind, string> = {
  snooze: "/api/os/email-actions/snooze",
  mute_deal: "/api/os/email-actions/mute-deal",
  not_interested: "/email-actions/not-interested",
  mute_kind: "/api/os/email-actions/mute-kind",
  stop: "/api/os/email-actions/stop",
  manage: "/api/os/email-actions/manage",
};

/** Per-deal actions expire. Stop, mute-category, and manage links do not. */
export const OS_EMAIL_ACTION_TOKEN_TTL = "30d";

const PERSISTENT_OS_EMAIL_ACTIONS = new Set<OsEmailActionKind>([
  "stop",
  "mute_kind",
  "manage",
]);

export async function createOsEmailActionToken(
  input: Omit<OsEmailActionToken, "purpose">,
): Promise<string> {
  const expiration = PERSISTENT_OS_EMAIL_ACTIONS.has(input.action)
    ? null
    : OS_EMAIL_ACTION_TOKEN_TTL;
  return encryptJWT(
    { purpose: OS_EMAIL_ACTION_PURPOSE, ...input },
    expiration,
  );
}

/** Returns null for malformed, expired, or foreign-purpose tokens. */
export async function readOsEmailActionToken(
  token: string,
): Promise<OsEmailActionToken | null> {
  try {
    const payload = await decryptJWT<Partial<OsEmailActionToken>>(token);
    if (payload.purpose !== OS_EMAIL_ACTION_PURPOSE) return null;
    if (!payload.userId || !payload.action) return null;
    return payload as OsEmailActionToken;
  } catch {
    return null;
  }
}

export async function buildOsEmailActionUrl(
  input: Omit<OsEmailActionToken, "purpose">,
): Promise<string> {
  const token = await createOsEmailActionToken(input);
  return buildUrl(
    `${ACTION_PATHS[input.action]}?token=${encodeURIComponent(token)}`,
  );
}

export type OsEmailActionLinks = {
  snooze?: string;
  muteDeal?: string;
  notInterested?: string;
  muteKind?: string;
  stop: string;
  manage: string;
};

/** Build every action link an email might render, in one pass. */
export async function buildOsEmailActionLinks(input: {
  userId: string;
  dealId?: string | null;
  preference?: OsNotificationPreference | null;
  includeDealActions: boolean;
}): Promise<OsEmailActionLinks> {
  const { userId } = input;
  const [stop, manage] = await Promise.all([
    buildOsEmailActionUrl({ userId, action: "stop" }),
    buildOsEmailActionUrl({ userId, action: "manage" }),
  ]);
  const links: OsEmailActionLinks = { stop, manage };
  if (input.preference) {
    links.muteKind = await buildOsEmailActionUrl({
      userId,
      action: "mute_kind",
      preference: input.preference,
    });
  }
  if (input.dealId && input.includeDealActions) {
    const dealId = input.dealId;
    const [snooze, muteDeal, notInterested] = await Promise.all([
      buildOsEmailActionUrl({ userId, action: "snooze", dealId }),
      buildOsEmailActionUrl({ userId, action: "mute_deal", dealId }),
      buildOsEmailActionUrl({ userId, action: "not_interested", dealId }),
    ]);
    links.snooze = snooze;
    links.muteDeal = muteDeal;
    links.notInterested = notInterested;
  }
  return links;
}
